<template>
  <div class="p-wizard">
    <div class="p-wizard__header">
      <el-stepper
        :value="activeStepIndex"
        horizontal
      >
        <el-stepper-item
          v-for="step in steps"
          :key="step.id"
          :title="step.title"
          :data-test-id="`wizard.stepper.item-${step.id}`"
        />
      </el-stepper>
      <div
        class="p-wizard__close"
        data-test-id="wizard.close"
      >
        <el-button
          type="ghost"
          @click="handleClose"
        >
          <el-icon name="i-times--24" />
        </el-button>
      </div>
    </div>
    <div class="p-wizard__content">
      <!--TODO: Add el-scrollbar-->
      <keep-alive>
        <component
          :is="activeStep.component.is"
          ref="activeStep"
          v-bind="activeStep.component.props"
          v-on="activeStep.component.handlers"
        />
      </keep-alive>
    </div>
    <div class="p-wizard__footer">
      <el-button
        v-if="isBackShown"
        type="secondary"
        data-test-id="wizard.button-back"
        @click="handleBackClick"
      >
        {{ $t('action.back') }}
      </el-button>
      <el-button
        v-if="isNextShown"
        :loading="isActiveStepPending"
        :disabled="isActiveStepDisabled"
        data-test-id="wizard.button-next"
        @click="handleNextClick"
      >
        {{ $t('action.next') }}
      </el-button>
      <el-button
        v-if="isDoneShown"
        :loading="isActiveStepPending"
        :disabled="isActiveStepDisabled"
        data-test-id="wizard.button-done"
        @click="handleDoneClick"
      >
        {{ $t('action.done') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { areContactRequiredFieldsFilled } from '@/app/domain/Contact';
import { getCompanyWizardStepValue, getContactsWizardStepValue } from '@/app/domain/Wizard';
import { redirectToConsole } from '@/library/redirectToConsole';
import CompanyWizardStep from '@/app/components/wizard/CompanyWizardStep';
import ContactsWizardStep from '@/app/components/wizard/ContactsWizardStep';
import { areCompanyRequiredFieldsFilled } from '@/app/domain/Company';

export default {
  name: 'Wizard',
  data() {
    return {
      steps: [],
      activeStepIndex: 0,
      isActiveStepPending: false,
      isActiveStepDisabled: false
    };
  },
  computed: {
    ...mapGetters('app', [
      'company',
      'addedContactList'
    ]),
    activeStep() {
      return this.steps[this.activeStepIndex];
    },
    isDoneShown() {
      return this.activeStepIndex === this.steps.length - 1;
    },
    isNextShown() {
      return this.activeStepIndex >= 0 && this.activeStepIndex < this.steps.length - 1;
    },
    isBackShown() {
      return this.activeStepIndex > 0;
    }
  },
  created() {
    this.steps = [
      {
        id: 'company',
        title: this.$t('wizard.company.title'),
        component: {
          is: CompanyWizardStep,
          props: {
            value: getCompanyWizardStepValue(this.company),
            stepIndex: 1
          },
          handlers: {
            input: this.handleValueChange
          }
        },
        save: this.saveCompanyWizardStep,
        isSaveDisabled: (value) => !areCompanyRequiredFieldsFilled(value)
      },
      {
        id: 'contacts',
        title: this.$t('wizard.contacts.title'),
        component: {
          is: ContactsWizardStep,
          props: {
            value: getContactsWizardStepValue(this.addedContactList),
            stepIndex: 2
          },
          handlers: {
            input: this.handleValueChange
          }
        },
        save: (values) => this.saveContactsWizardStep(values),
        isSaveDisabled: (values) => values.some((value) => !areContactRequiredFieldsFilled(value))
      }
    ];
  },
  methods: {
    ...mapActions('app', [
      'saveCompanyWizardStep',
      'saveContactsWizardStep'
    ]),
    handleValueChange(value) {
      this.activeStep.component.props.value = value;
      this.isActiveStepDisabled = this.activeStep.isSaveDisabled(value);
    },
    async handleDoneClick() {
      const isActiveStepValid = await this.$refs.activeStep.validate();

      if (isActiveStepValid) {
        await this.saveActiveStep();
        redirectToConsole();
      }
    },
    async handleNextClick() {
      if (this.activeStepIndex < this.steps.length) {
        const isActiveStepValid = await this.$refs.activeStep.validate();

        if (isActiveStepValid) {
          await this.saveActiveStep();
          ++this.activeStepIndex;
          this.isActiveStepDisabled = this.activeStep.isSaveDisabled(this.activeStep.component.props.value);
        }
      }
    },
    handleBackClick() {
      if (this.activeStepIndex > 0) {
        --this.activeStepIndex;
        this.isActiveStepDisabled = this.activeStep.isSaveDisabled(this.activeStep.component.props.value);
      }
    },
    handleClose() {
      redirectToConsole();
    },
    async saveActiveStep() {
      this.isActiveStepPending = true;
      await this.activeStep.save(this.activeStep.component.props.value);
      this.isActiveStepPending = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@uikit/ui-kit/themes/acronis/src/common/variables';

.p-wizard {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__header,
  &__footer {
    display: flex;
    align-items: center;
    position: relative;
    height: 64px;
    padding: 0 24px;
    background-color: $av-fixed-white;
    background-clip: padding-box;
  }

  &__header {
    justify-content: center;
    border-bottom: $av-border-sm $av-brand-accent;
  }

  &__close {
    position: absolute;
    top: 16px;
    right: 16px;
  }

  &__content {
    flex: 1;
    overflow: auto;
  }

  &__footer {
    justify-content: flex-end;
    border-top: $av-border-sm $av-brand-accent;

    .el-button {
      margin-left: 16px;
    }
  }
}
</style>
